import * as React from 'react';
import { Route, BrowserRouter, Switch } from 'react-router-dom';

import App from './components/App';
import AuthRoute from './components/AuthRoute';
import AdminRoute from './components/AdminRoute';
import Masthead from './components/Masthead';
import ScrollToTop from './components/ScrollToTop';

import AcceptInvitation from './pages/AcceptInvitation';
import BusinessHome from './pages/BusinessHome';
import Dashboard from './pages/dashboard/Dashboard';
import EditCompany from './pages/EditCompany';
import EditGuide from './pages/EditGuide';
import EditProfile from './pages/EditProfile';
import Feeds from './pages/Feeds';
import ForgotPassword from './pages/ForgotPassword';
import GuidesHome from './pages/GuidesHome';
import Login from './pages/Login';
import NewCompany from './pages/NewCompany';
import PrefetchFeed from './pages/PrefetchFeed';
import PrefetchProduct from './pages/products/PrefetchProduct';
import Products from './pages/products/Products';
import Register from './pages/Register';
import ResetPassword from './pages/ResetPassword';
import Today from './pages/Today';
import VerifyUser from './pages/VerifyUser';
import ConfirmUserDeletion from './pages/ConfirmUserDeletion';

import { isHostBusiness, isHostGuides } from './utils/host';
import { ProfileContextProvider } from './profile';
import CompanyRoute from './components/CompanyRoute';
import OwnGuide from './pages/OwnGuide';
import PrefetchProductVariant from './pages/products/PrefetchProductVariant';
import ProductVariants from './pages/products/ProductVariants';
import PublicGuide from './pages/PublicGuide';
import VariantStock from './pages/products/VariantStock';

import Categories from './pages/Categories';
import PrefetchCategory from './pages/PrefetchCategory';
import PrefetchContract from './pages/PrefetchContract';
import Contracts from './pages/Contracts';
import EditBillingInfo from './pages/EditBillingInfo';

import CancelOrderShipment from './pages/orders/CancelOrderShipment';
import Orders from './pages/orders/Orders';
import PrefetchOrder from './pages/orders/PrefetchOrder';
import ShipOrderShipment from './pages/orders/ShipOrderShipment';

import HoldOrderShipment from './pages/orders/HoldOrderShipment';
import Invitations from './pages/Invitations';
import NewInvitation from './pages/NewInvitation';
import PrefetchShippingMethod from './pages/PrefetchShippingMethod';
import ShippingMethods from './pages/ShippingMethods';
import Guides from './pages/Guides';
import SalesReports from './pages/sales-reports/SalesReports';
import SalesReport from './pages/sales-reports/SalesReport';
import PrefetchSalesReport from './pages/sales-reports/PrefetchSalesReport';

const Base = (data: any) => (
  <ProfileContextProvider>
    <ScrollToTop>{data.children}</ScrollToTop>
  </ProfileContextProvider>
);

const HomeResolver = (props: any) => {
  if (isHostBusiness()) {
    return <BusinessHome {...props} />;
  }

  if (isHostGuides()) {
    return (
      <App>
        <Masthead />
        <GuidesHome {...props} />
      </App>
    );
  }

  return <Login {...props} />;
};

const PanelRoutes = () => (
  <Switch>
    {/* User */}
    <AuthRoute path="/dashboard" component={Dashboard} />
    <AuthRoute path="/profile" component={EditProfile} />
    {/* Company */}
    <AuthRoute path="/company/new" component={NewCompany} />
    <AuthRoute path="/company" component={EditCompany} />
    {/* Invitations */}
    <AuthRoute path="/invitations/new" component={NewInvitation} />
    {/* <AuthRoute path="/invitations/:id/delete" component={DeleteInvitation} /> */}
    <AuthRoute path="/invitations" component={Invitations} />
    {/* Billing */}
    <CompanyRoute path="/billing" component={EditBillingInfo} />
    {/* Shipping Methods */}
    <CompanyRoute
      path="/shipping-methods/:id"
      component={PrefetchShippingMethod}
    />
    <CompanyRoute path="/shipping-methods" component={ShippingMethods} />
    {/* Stock */}
    <CompanyRoute
      path="/products/:productId/variants/:variantId/stock"
      component={VariantStock}
    />
    {/* Variants */}
    <CompanyRoute
      path="/products/:productId/variants/:variantId"
      component={PrefetchProductVariant}
    />
    <CompanyRoute
      path="/products/:productId/variants"
      component={ProductVariants}
    />
    {/* Products */}
    <CompanyRoute path="/products/new" component={PrefetchProduct} />
    <CompanyRoute path="/products/:productId" component={PrefetchProduct} />
    <CompanyRoute path="/products" component={Products} />
    {/* Sales Reports */}
    <CompanyRoute path="/sales-reports/:reportId" component={PrefetchSalesReport} />
    <CompanyRoute path="/sales-reports" component={SalesReports} />
    {/* Order management */}
    <CompanyRoute
      path="/orders/:id/shipments/:shipmentid/cancel"
      component={CancelOrderShipment}
    />
    <CompanyRoute
      path="/orders/:id/shipments/:shipmentid/hold"
      component={HoldOrderShipment}
    />
    <CompanyRoute
      path="/orders/:id/shipments/:shipmentid/ship"
      component={ShipOrderShipment}
    />
    <CompanyRoute path="/orders/:id" component={PrefetchOrder} />
    <CompanyRoute path="/orders" component={Orders} />
    {/* Guides */}
    <AuthRoute path="/guides/edit/:guideId" component={EditGuide} />
    <AuthRoute path="/guides/:guideId" component={OwnGuide} />
    <AuthRoute path="/guides" component={Guides} />
    {/* Admin… */}
    <AdminRoute path="/feeds/edit/:feedId" component={PrefetchFeed} />
    <AdminRoute path="/feeds" component={Feeds} />
    <AdminRoute path="/categories/:categoryId" component={PrefetchCategory} />
    <AdminRoute path="/categories" component={Categories} />
    <AdminRoute path="/contracts/:id" component={PrefetchContract} />
    <AdminRoute path="/contracts" component={Contracts} />
    {/* TODO: Add some sort of filtering for robots phrases */}
    <Route path="/:name" component={PublicGuide} />
  </Switch>
);

export const Routes = () => (
  <BrowserRouter>
    <Base>
      <Switch>
        <Route exact={true} path="/" component={HomeResolver} />
        <Route path="/today" component={Today} />
        <Route path="/login" component={Login} />
        <Route path="/register" component={Register} />
        <Route path="/forgot-password" component={ForgotPassword} />
        <Route path="/password-reset/:token" component={ResetPassword} />
        <Route path="/verify/:token" component={VerifyUser} />
        <Route path="/accept/:token" component={AcceptInvitation} />
        <Route path="/confirm-delete/:token" component={ConfirmUserDeletion} />
        <div>
          <Masthead />
          <App>
            <PanelRoutes />
          </App>
        </div>
      </Switch>
    </Base>
  </BrowserRouter>
);
